import React, { useState, useEffect, useRef } from 'react';
import { Shield, AlertCircle, Smartphone, Download, QrCode, CheckCircle, Loader2, X } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import QRCode from 'qrcode';

interface InvitedUser2FASetupModalProps {
  accessToken: string;
  onComplete: (userId: string) => void;
  onCancel: () => void;
}

type Step = 'qr' | 'verify' | 'success';

const InvitedUser2FASetupModal: React.FC<InvitedUser2FASetupModalProps> = ({
  accessToken,
  onComplete,
  onCancel,
}) => {
  const [step, setStep] = useState<Step>('qr');
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  // Push a history entry so browser back triggers sign-out instead of navigating away.
  useEffect(() => {
    window.history.pushState({ twofa_setup: true }, '', window.location.href);

    const handlePopState = () => {
      if (!cancelledRef.current) {
        cancelledRef.current = true;
        handleCancel();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    initSetup();
  }, []);

  const getToken = async (): Promise<string> => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || accessToken;
  };

  const handleCancel = async () => {
    cancelledRef.current = true;
    await supabase.auth.signOut();
    onCancel();
  };

  const initSetup = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twofa-setup`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        }
      );

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to generate QR code');
      }

      const data = await response.json();
      const qrDataUrl = await QRCode.toDataURL(data.otpauth_url, { width: 240, margin: 2 });
      setQrCodeUrl(qrDataUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set up 2FA. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setCode(value);
      setError(null);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setIsLoading(true);
    setError(null);
    let verifiedUserId: string | null = null;

    try {
      const token = await getToken();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twofa-verify`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ token: code, enable: true }),
        }
      );

      if (!response.ok) {
        const errData = await response.json();
        setError(errData.error || 'Invalid code. Please check your authenticator app and try again.');
        return;
      }

      // Edge function stamped twofa_verified=true via service role; refresh so JWT picks it up.
      const { data: refreshed } = await supabase.auth.refreshSession();
      verifiedUserId = refreshed.session?.user?.id ?? null;
      setStep('success');
    } catch {
      setError('Failed to verify code. Please try again.');
    } finally {
      setIsLoading(false);
    }

    if (verifiedUserId) {
      setTimeout(() => {
        onComplete(verifiedUserId!);
      }, 2000);
    }
  };

  const stepLabel = step === 'qr' ? 'Step 1 of 2 — Scan QR Code' : step === 'verify' ? 'Step 2 of 2 — Verify Code' : 'Complete';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

        <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-6 py-5">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#40E0D0]/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-[#40E0D0]" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-lg leading-tight">Two-Factor Authentication Setup</h2>
                <p className="text-gray-400 text-xs mt-0.5">Required for your account — one time only</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10 flex-shrink-0 ml-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {step !== 'success' && (
            <div className="mt-4 flex items-center gap-2">
              <div className={`h-1.5 flex-1 rounded-full ${step === 'qr' || step === 'verify' ? 'bg-[#40E0D0]' : 'bg-gray-600'}`} />
              <div className={`h-1.5 flex-1 rounded-full ${step === 'verify' ? 'bg-[#40E0D0]' : 'bg-gray-600'}`} />
              <span className="text-xs text-gray-400 ml-1 whitespace-nowrap">{stepLabel}</span>
            </div>
          )}
        </div>

        <div className="px-6 py-6">

          {step === 'qr' && (
            <div className="space-y-5">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
                <Download className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  <strong>Don't have the app yet?</strong> Download <strong>Google Authenticator</strong> from the App Store (iPhone) or Google Play (Android) before scanning.
                </p>
              </div>

              <div className="text-center">
                <p className="text-gray-700 text-sm mb-4">
                  Open <strong>Google Authenticator</strong>, tap <strong className="text-[#40E0D0]">+</strong>, choose <strong>Scan a QR code</strong>, then scan below:
                </p>

                {isLoading ? (
                  <div className="flex flex-col items-center justify-center h-52 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                    <Loader2 className="animate-spin h-10 w-10 text-[#40E0D0] mb-3" />
                    <p className="text-sm text-gray-500">Generating your QR code...</p>
                  </div>
                ) : error ? (
                  <div className="space-y-3">
                    <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg text-left">
                      <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                    <button
                      type="button"
                      onClick={initSetup}
                      className="w-full bg-gray-100 text-gray-700 font-medium py-2.5 rounded-xl hover:bg-gray-200 transition-colors text-sm"
                    >
                      Try Again
                    </button>
                  </div>
                ) : qrCodeUrl ? (
                  <div className="space-y-4">
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-4 inline-block mx-auto shadow-sm">
                      <img
                        src={qrCodeUrl}
                        alt="Scan this QR code in Google Authenticator"
                        className="w-52 h-52 block"
                      />
                    </div>
                  </div>
                ) : null}
              </div>

              {!isLoading && !error && qrCodeUrl && (
                <button
                  type="button"
                  onClick={() => setStep('verify')}
                  className="w-full bg-[#40E0D0] text-black font-semibold py-3 rounded-xl hover:bg-[#40E0D0]/90 transition-colors flex items-center justify-center gap-2"
                >
                  <Smartphone className="w-4 h-4" />
                  I've Scanned the Code — Enter My Code
                </button>
              )}

              <button
                type="button"
                onClick={handleCancel}
                className="w-full bg-gray-100 text-gray-500 font-medium py-2.5 rounded-xl hover:bg-gray-200 transition-colors text-sm"
              >
                Cancel & Sign Out
              </button>
            </div>
          )}

          {step === 'verify' && (
            <div className="space-y-5">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 mb-3">
                  <svg viewBox="0 0 48 48" className="w-14 h-14">
                    <path d="M24 4L8 14L4 24L8 34L24 44L40 34L44 24L40 14L24 4Z" fill="#EA4335" />
                    <path d="M24 4L40 14L44 24L40 34L24 44L24 4Z" fill="#4285F4" />
                    <path d="M4 24L8 14L24 4L24 44L8 34L4 24Z" fill="#34A853" />
                    <path d="M24 44L8 34L4 24L8 14L24 4L24 44Z" fill="#FBBC04" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">Enter the 6-digit code</h3>
                <p className="text-gray-500 text-sm">
                  Open Google Authenticator and enter the code now showing for <strong>Perplexity</strong>.
                </p>
              </div>

              <form onSubmit={handleVerify} className="space-y-4">
                <div>
                  <input
                    type="text"
                    value={code}
                    onChange={handleCodeChange}
                    placeholder="000000"
                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl text-center text-3xl tracking-[0.5em] font-mono focus:ring-2 focus:ring-[#40E0D0] focus:border-[#40E0D0] outline-none transition-colors bg-white text-gray-900 placeholder-gray-300"
                    maxLength={6}
                    autoComplete="off"
                    autoFocus
                    inputMode="numeric"
                  />
                </div>

                {error && (
                  <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading || code.length !== 6}
                  className="w-full bg-[#40E0D0] text-black font-semibold py-3 rounded-xl hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin mr-2 h-5 w-5" />
                      Verifying...
                    </>
                  ) : (
                    'Activate 2FA & Access Dashboard'
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => { setStep('qr'); setError(null); }}
                  className="w-full bg-gray-100 text-gray-500 font-medium py-2.5 rounded-xl hover:bg-gray-200 transition-colors text-sm flex items-center justify-center gap-1"
                >
                  <QrCode className="w-3.5 h-3.5" />
                  Back to QR Code
                </button>
              </form>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs text-amber-700">
                  <strong>Tip:</strong> Codes refresh every 30 seconds. If the code doesn't work, wait for the next one and try again.
                </p>
              </div>
            </div>
          )}

          {step === 'success' && (
            <div className="flex flex-col items-center py-6 space-y-4 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-9 h-9 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">2FA Activated!</h3>
              <p className="text-gray-600 text-sm max-w-xs">
                Your account is now secured with two-factor authentication. You'll be asked for a code each time you log in.
              </p>
              <Loader2 className="animate-spin h-5 w-5 text-[#40E0D0]" />
              <p className="text-xs text-gray-400">Redirecting to your dashboard...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvitedUser2FASetupModal;
